import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getBackendUrl } from "@/lib/backend";
import { useScope } from "@/context/ScopeContext";
import CtSchoolsModalFilterToolbar from "@/components/CtSchoolsModalFilterToolbar";
import { useCtSchoolsModalFilters } from "@/hooks/useCtSchoolsModalFilters";
import "./UsmRiskSchoolsModal.css";

const API = `${getBackendUrl()}/api`;
const PAGE_SIZE = 12;

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");
const GAP_LABEL_SEARCH_KEYS = ["gap_label"];

const CWSN_CATEGORY = "no_cwsn_access";

const EquityInclusionSchoolsModal = ({ open, category, onClose, scopeLabel }) => {
  const navigate = useNavigate();
  const { scope } = useScope();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const {
    blockFilter,
    setBlockFilter,
    schoolFilter,
    setSchoolFilter,
    blockOptions,
    schoolOptions,
    filtered,
  } = useCtSchoolsModalFilters(data?.schools || [], query, GAP_LABEL_SEARCH_KEYS);

  useEffect(() => {
    if (!open || !category?.id) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setQuery("");
    setBlockFilter("");
    setSchoolFilter("");
    setPage(1);

    const params = { category: category.id, limit: 2000 };
    if (scope.districtCode) params.district_code = scope.districtCode;
    if (scope.blockCode) params.block_code = scope.blockCode;
    if (scope.udiseCode) params.udise_code = scope.udiseCode;
    if (scope.districtName) params.district_name = scope.districtName;
    if (scope.blockName) params.block_name = scope.blockName;
    if (!scope.districtName && !scope.districtCode) params.district_name = "PUNE";

    axios
      .get(`${API}/classrooms-toilets/equity-inclusion-schools`, { params })
      .then((res) => {
        if (!cancelled) setData(res.data || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.detail || "Failed to load school list");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    category?.id,
    scope.version,
    scope.districtCode,
    scope.blockCode,
    scope.udiseCode,
    scope.districtName,
    scope.blockName,
  ]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const parameterLabel = data?.parameter_label || category?.label || "Count";
  const isCwsn = category?.id === CWSN_CATEGORY;

  if (!open || !category) return null;

  return createPortal(
    <div className="usm-risk-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="usm-risk-modal usm-risk-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ct-equity-inclusion-schools-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="usm-risk-modal-head">
          <div>
            <p className="usm-risk-modal-eyebrow">Equity & Inclusion drill-down · {scopeLabel || "District scope"}</p>
            <h3 id="ct-equity-inclusion-schools-modal-title">{category.label}</h3>
            <p className="usm-risk-modal-sub">
              Aggregate: <b>{category.value}</b>
              {data?.description ? <> · {data.description}</> : null}
            </p>
          </div>
          <button type="button" className="usm-risk-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <CtSchoolsModalFilterToolbar
          blockFilter={blockFilter}
          setBlockFilter={setBlockFilter}
          schoolFilter={schoolFilter}
          setSchoolFilter={setSchoolFilter}
          blockOptions={blockOptions}
          schoolOptions={schoolOptions}
          loading={loading}
          filteredCount={filtered.length}
          totalCount={data?.total_schools}
          onFilterChange={() => setPage(1)}
        />

        <div className="usm-risk-modal-body">
          {loading ? (
            <div className="usm-risk-modal-loading">Loading schools…</div>
          ) : error ? (
            <div className="usm-risk-modal-error">{error}</div>
          ) : !data?.schools?.length ? (
            <div className="usm-risk-modal-empty">No schools found for this category in the current scope.</div>
          ) : (
            <>
              <div className="usm-risk-modal-body-search">
                <input
                  type="search"
                  className="usm-risk-modal-search"
                  placeholder="Search school, block, district, or UDISE…"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              {!pageRows.length ? (
                <div className="usm-risk-modal-empty">No schools match the selected block, school, or search.</div>
              ) : (
                <table className="usm-risk-modal-table usm-risk-modal-table-wide">
                  <thead>
                    <tr>
                      <th>School</th>
                      <th>Block</th>
                      <th>UDISE</th>
                      {isCwsn ? (
                        <>
                          <th>{parameterLabel}</th>
                          <th className="usm-num">CWSN boys</th>
                          <th className="usm-num">CWSN girls</th>
                        </>
                      ) : (
                        <th>{parameterLabel}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((school) => (
                      <tr
                        key={school.udise_code}
                        className="usm-risk-modal-row"
                        onClick={() => school.udise_code && navigate(`/school/${school.udise_code}`)}
                        title={school.udise_code ? "Open school detail" : undefined}
                      >
                        <td>{school.school_name || "—"}</td>
                        <td>{school.block_name || "—"}</td>
                        <td>{school.udise_code || "—"}</td>
                        {isCwsn ? (
                          <>
                            <td>{school.gap_label || "—"}</td>
                            <td className="usm-num">{fmt(school.cwsn_boys_total)}</td>
                            <td className="usm-num">{fmt(school.cwsn_girls_total)}</td>
                          </>
                        ) : (
                          <td>{school.gap_label || "—"}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        {!loading && !error && filtered.length > PAGE_SIZE ? (
          <div className="usm-risk-modal-footer">
            <button
              type="button"
              className="usm-risk-modal-page-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="usm-risk-modal-page-btn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
};

export default EquityInclusionSchoolsModal;
